/**
 * Dashboard chart presenter'i
 */

import Chart from 'chart.js/auto';
import { AppState } from '../../modules/state.js';
import { CATEGORY_LABELS, MONTHS_SHORT } from '../../firebase-config.js';

interface DashboardChartsPresenterDeps {
    formatNumber: (value: number) => string;
}

function calculateBalance(): number {
    const income = AppState.transactions.filter(transaction => transaction.type === 'income').reduce((sum, transaction) => sum + transaction.amount, 0);
    const expense = AppState.transactions.filter(transaction => transaction.type === 'expense').reduce((sum, transaction) => sum + transaction.amount, 0);
    return income - expense;
}

function getMonthlyData(year: number) {
    const data = { income: Array(12).fill(0), expense: Array(12).fill(0) };
    AppState.transactions.forEach(transaction => {
        const date = new Date(transaction.date);
        if (date.getFullYear() === year) {
            const month = date.getMonth();
            if (transaction.type === 'income') data.income[month] += transaction.amount;
            else data.expense[month] += transaction.amount;
        }
    });
    return data;
}

function getCategoryBreakdown(type: 'income' | 'expense') {
    const breakdown: Record<string, number> = {};
    AppState.transactions.filter(transaction => transaction.type === type).forEach(transaction => {
        breakdown[transaction.category] = (breakdown[transaction.category] || 0) + transaction.amount;
    });
    return breakdown;
}

function getDuesCollectionData(year: number) {
    const data = { collected: Array(12).fill(0), total: Array(12).fill(0), rate: Array(12).fill(0) };
    const amount = AppState.settings.monthlyDueAmount;
    const totalApartments = AppState.apartments.length || 12;

    for (let month = 1; month <= 12; month++) {
        let paid = 0;
        for (let apartment = 1; apartment <= totalApartments; apartment++) {
            if (AppState.dues[year]?.[apartment]?.[month]) paid++;
        }
        data.collected[month - 1] = paid * amount;
        data.total[month - 1] = totalApartments * amount;
        data.rate[month - 1] = totalApartments > 0 ? Math.round((paid / totalApartments) * 100) : 0;
    }

    return data;
}

export function destroyAllChartsPresenter(): void {
    Object.values(AppState.charts).forEach(chart => {
        if (chart) (chart as any).destroy();
    });
    AppState.charts = {};
}

export function initResidentChartsPresenter(deps: DashboardChartsPresenterDeps): void {
    destroyAllChartsPresenter();
    setTimeout(() => {
        initDuesCollectionChartPresenter();
        initFinancialStatusChartPresenter(deps);
        initIncomePieChartPresenter();
        initExpensePieChartPresenter();
    }, 100);
}

export function initDuesCollectionChartPresenter(): void {
    const canvas = document.getElementById('dues-collection-chart') as HTMLCanvasElement | null;
    if (!canvas) return;

    const context = canvas.getContext('2d');
    if (!context) return;

    const data = getDuesCollectionData(AppState.currentYear);
    const gradientBlue = context.createLinearGradient(0, 0, 0, 400);
    gradientBlue.addColorStop(0, 'rgba(56, 189, 248, 0.9)');
    gradientBlue.addColorStop(0.5, 'rgba(59, 130, 246, 0.8)');
    gradientBlue.addColorStop(1, 'rgba(99, 102, 241, 0.7)');

    (AppState.charts as any).duesCollection = new Chart(canvas, {
        type: 'bar',
        data: {
            labels: MONTHS_SHORT,
            datasets: [{
                label: 'Tahsilat Oranı (%)',
                data: data.rate,
                backgroundColor: gradientBlue,
                borderColor: 'rgba(56, 189, 248, 1)',
                borderWidth: 2,
                borderRadius: 8,
                borderSkipped: false,
                hoverBackgroundColor: 'rgba(56, 189, 248, 1)'
            }]
        },
        options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                legend: { display: false },
                tooltip: {
                    backgroundColor: 'rgba(30, 41, 59, 0.95)',
                    titleColor: '#fff',
                    bodyColor: '#fff',
                    borderColor: 'rgba(56, 189, 248, 0.5)',
                    borderWidth: 1,
                    cornerRadius: 8,
                    padding: 12
                }
            },
            scales: {
                y: {
                    beginAtZero: true,
                    max: 100,
                    ticks: {
                        color: 'rgba(100, 116, 139, 0.8)',
                        callback: value => `${value}%`,
                        font: { size: 11 }
                    },
                    grid: { color: 'rgba(148, 163, 184, 0.1)' }
                },
                x: {
                    ticks: {
                        color: 'rgba(100, 116, 139, 0.8)',
                        font: { size: 11 }
                    },
                    grid: { display: false }
                }
            }
        }
    });
}

export function initFinancialStatusChartPresenter(deps: DashboardChartsPresenterDeps): void {
    const canvas = document.getElementById('financial-status-chart') as HTMLCanvasElement | null;
    if (!canvas) return;

    const context = canvas.getContext('2d');
    if (!context) return;

    const data = getMonthlyData(AppState.currentYear);
    const balance = calculateBalance();

    const balanceEl = document.getElementById('current-balance');
    if (balanceEl) {
        balanceEl.textContent = `₺${deps.formatNumber(balance)}`;
        balanceEl.className = `chart-stat-value ${balance < 0 ? 'negative' : ''}`;
    }

    const gradientGreen = context.createLinearGradient(0, 0, 0, 300);
    gradientGreen.addColorStop(0, 'rgba(16, 185, 129, 0.4)');
    gradientGreen.addColorStop(1, 'rgba(16, 185, 129, 0.05)');

    const gradientRed = context.createLinearGradient(0, 0, 0, 300);
    gradientRed.addColorStop(0, 'rgba(249, 115, 22, 0.4)');
    gradientRed.addColorStop(1, 'rgba(239, 68, 68, 0.05)');

    (AppState.charts as any).financialStatus = new Chart(canvas, {
        type: 'line',
        data: {
            labels: MONTHS_SHORT,
            datasets: [
                {
                    label: 'Gelir',
                    data: data.income,
                    borderColor: 'rgb(16, 185, 129)',
                    backgroundColor: gradientGreen,
                    fill: true,
                    tension: 0.4,
                    borderWidth: 3,
                    pointBackgroundColor: 'rgb(16, 185, 129)',
                    pointBorderColor: '#fff',
                    pointBorderWidth: 2,
                    pointRadius: 4,
                    pointHoverRadius: 6
                },
                {
                    label: 'Gider',
                    data: data.expense,
                    borderColor: 'rgb(249, 115, 22)',
                    backgroundColor: gradientRed,
                    fill: true,
                    tension: 0.4,
                    borderWidth: 3,
                    pointBackgroundColor: 'rgb(249, 115, 22)',
                    pointBorderColor: '#fff',
                    pointBorderWidth: 2,
                    pointRadius: 4,
                    pointHoverRadius: 6
                }
            ]
        },
        options: {
            responsive: true,
            maintainAspectRatio: false,
            interaction: {
                intersect: false,
                mode: 'index'
            },
            plugins: {
                legend: {
                    display: true,
                    position: 'top',
                    labels: {
                        color: 'rgba(100, 116, 139, 0.9)',
                        usePointStyle: true,
                        pointStyle: 'circle',
                        padding: 20,
                        font: { size: 12, weight: 'bold' }
                    }
                },
                tooltip: {
                    backgroundColor: 'rgba(30, 41, 59, 0.95)',
                    titleColor: '#fff',
                    bodyColor: '#fff',
                    borderColor: 'rgba(148, 163, 184, 0.3)',
                    borderWidth: 1,
                    cornerRadius: 8,
                    padding: 12,
                    callbacks: {
                        label: contextItem => `${contextItem.dataset.label}: ₺${deps.formatNumber(Number(contextItem.raw) || 0)}`
                    }
                }
            },
            scales: {
                y: {
                    beginAtZero: true,
                    ticks: {
                        color: 'rgba(100, 116, 139, 0.8)',
                        callback: value => `₺${deps.formatNumber(Number(value) || 0)}`,
                        font: { size: 11 }
                    },
                    grid: { color: 'rgba(148, 163, 184, 0.1)' }
                },
                x: {
                    ticks: {
                        color: 'rgba(100, 116, 139, 0.8)',
                        font: { size: 11 }
                    },
                    grid: { display: false }
                }
            }
        }
    });
}

function renderPieChartState(canvas: HTMLCanvasElement, emptyText: string, labels: string[], values: number[], colors: string[], chartKey: 'incomePie' | 'expensePie'): void {
    const container = canvas.parentElement;
    if (!container) return;

    if (AppState.charts[chartKey]) {
        AppState.charts[chartKey]!.destroy();
        AppState.charts[chartKey] = null;
    }

    if (values.length === 0) {
        canvas.style.display = 'none';
        let emptyMsg = container.querySelector('.chart-empty-state');
        if (!emptyMsg) {
            emptyMsg = document.createElement('p');
            emptyMsg.className = 'empty-state chart-empty-state';
            emptyMsg.textContent = emptyText;
            container.appendChild(emptyMsg);
        }
        return;
    }

    const emptyMsg = container.querySelector('.chart-empty-state');
    if (emptyMsg) emptyMsg.remove();
    canvas.style.display = 'block';

    AppState.charts[chartKey] = new Chart(canvas, {
        type: 'doughnut',
        data: {
            labels,
            datasets: [{ data: values, backgroundColor: colors, borderWidth: 0 }]
        },
        options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                legend: {
                    position: 'bottom',
                    labels: { color: 'rgba(255,255,255,0.7)', padding: 15 }
                }
            }
        }
    });
}

export function initIncomePieChartPresenter(): void {
    const canvas = document.getElementById('income-pie-chart') as HTMLCanvasElement | null;
    if (!canvas) return;

    const breakdown = getCategoryBreakdown('income');
    const labels = Object.keys(breakdown).map(key => CATEGORY_LABELS[key] || key);
    const values = Object.values(breakdown);
    renderPieChartState(canvas, 'Gelir verisi yok', labels, values, ['#22c55e', '#14b8a6', '#10b981', '#059669'], 'incomePie');
}

export function initExpensePieChartPresenter(): void {
    const canvas = document.getElementById('expense-pie-chart') as HTMLCanvasElement | null;
    if (!canvas) return;

    const breakdown = getCategoryBreakdown('expense');
    const labels = Object.keys(breakdown).map(key => CATEGORY_LABELS[key] || key);
    const values = Object.values(breakdown);
    renderPieChartState(canvas, 'Gider verisi yok', labels, values, ['#ef4444', '#f97316', '#f59e0b', '#eab308', '#ec4899', '#a855f7', '#6366f1', '#3b82f6'], 'expensePie');
}
